/**
 * Meta first-party cookies + persisted fbc from fbclid (survives SPA navigation after URL cleanup).
 * Improves CAPI Event Match Quality when forwarded in track-event payload.
 */

const FBC_STORAGE_KEY = "fastsell_meta_fbc_v1";

function readStoredFbc(): string | undefined {
  if (typeof localStorage === "undefined") return undefined;
  try {
    const s = localStorage.getItem(FBC_STORAGE_KEY)?.trim();
    if (s && s.length <= 500 && /^fb\.\d+\./i.test(s)) return s;
  } catch {
    /* private mode */
  }
  return undefined;
}

function writeStoredFbc(fbc: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(FBC_STORAGE_KEY, fbc);
  } catch {
    /* quota / private mode */
  }
}

/** If current URL has fbclid, build Meta-format fbc and persist (call early in session). */
export function persistFbclidFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const fbclid = new URL(window.location.href).searchParams.get("fbclid");
    if (!fbclid || !/^[a-zA-Z0-9_-]{8,512}$/.test(fbclid)) return;
    const fbc = `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`;
    writeStoredFbc(fbc);
  } catch {
    /* ignore */
  }
}

export function getFacebookBrowserIds(): { fbp?: string; fbc?: string; fbclid?: string } {
  if (typeof document === "undefined") return {};
  persistFbclidFromUrl();

  const fromCookie = (name: string): string | undefined => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m?.[1] ? decodeURIComponent(m[1]) : undefined;
  };
  let fbp = fromCookie("_fbp");
  let fbc = fromCookie("_fbc") ?? readStoredFbc();
  let fbclid: string | undefined;
  if (!fbc && typeof window !== "undefined") {
    try {
      const raw = new URL(window.location.href).searchParams.get("fbclid");
      if (raw && /^[a-zA-Z0-9_-]{8,512}$/.test(raw)) {
        fbclid = raw;
        fbc = `fb.1.${Math.floor(Date.now() / 1000)}.${raw}`;
        writeStoredFbc(fbc);
      }
    } catch {
      /* ignore */
    }
  }

  const out: { fbp?: string; fbc?: string; fbclid?: string } = {};
  if (fbp && fbp.length <= 500) out.fbp = fbp;
  if (fbc && fbc.length <= 500) out.fbc = fbc;
  if (fbclid) out.fbclid = fbclid;
  return out;
}

/** Safe merge for analytics / track-event payloads (consent handled by callers). */
export function facebookAttributionPayload(): Record<string, string> {
  const { fbp, fbc, fbclid } = getFacebookBrowserIds();
  const o: Record<string, string> = {};
  if (fbp) o.fbp = fbp;
  if (fbc) o.fbc = fbc;
  if (fbclid) o.fbclid = fbclid;
  return o;
}
